import { useCallback } from 'react';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useHelpDeskCreateTicket from '@ubeya/shared/hooks/account/useHelpDeskCreateTicket';
import useToaster from '@ubeya/shared/hooks/useToaster';
import { HELP_DESK_PRIORITIES } from '@ubeya/shared/constants/helpDesk';
import { useHelpDeskAnalytics } from '../../../helpDeskAnalytics';

/**
 * Builds the create-ticket body from the accumulated wizard values + step-3 form values, submits it,
 * surfaces failures via the toaster, tracks the submit event, and hands the new ticket to onCreated.
 */
const useCreateTicketSubmit = ({ currentValues, onCreated }) => {
  const { accountId } = useAccount();
  const { createTicketAsync } = useHelpDeskCreateTicket({ accountId });
  const { trackHelpDeskEvent } = useHelpDeskAnalytics();
  const { addResponseError } = useToaster();

  return useCallback(
    async (values) => {
      const body = {
        employeeId: currentValues.employeeId,
        branchId: currentValues.branchId,
        categoryId: currentValues.categoryId,
        title: (values.title || '').trim(),
        description: values.description?.trim() || null,
        attachments: currentValues.attachments || [],
        assignedAdminId: currentValues.assignedAdminId ?? null,
        ...(currentValues.priority &&
          currentValues.priority !== HELP_DESK_PRIORITIES.NONE && { priority: currentValues.priority }),
        ...(currentValues.shiftId && { shiftId: currentValues.shiftId })
      };

      try {
        const result = await createTicketAsync(body);
        if (!result?.data) {
          addResponseError();
          return;
        }

        trackHelpDeskEvent({
          eventName: 'Help desk - Admin submitted a ticket',
          context: 'Create ticket modal',
          eventProperties: {
            employeeId: currentValues.employeeId,
            branchId: currentValues.branchId,
            categoryId: currentValues.categoryId,
            priority: currentValues.priority,
            isShiftRelated: !!currentValues.shiftId,
            assignedAdminId: currentValues.assignedAdminId
          }
        });

        onCreated(result.data);
      } catch (error) {
        addResponseError(error);
      }
    },
    [createTicketAsync, trackHelpDeskEvent, onCreated, currentValues, addResponseError]
  );
};

export default useCreateTicketSubmit;
